import type {
  DOMConversionMap,
  DOMConversionOutput,
  EditorConfig,
  LexicalNode,
  NodeKey,
  SerializedElementNode,
  Spread,
} from 'lexical';
import { LexicalEditor } from 'lexical';

import { addClassNamesToElement } from '@lexical/utils';
import { LayoutContainerNode } from './LayoutContainerNode';
import { $createTabItemNode } from './TabItemNode.ts';
import { createActionButton } from '../utils/actionButton.ts';
import { $createLocalizationLinkNode } from './Localizable/LocalizationLinkNode.ts';
import { $createLocalizableTextNode } from './Localizable/LocalizableTextNode.tsx';
import i18n from 'i18next';
import { $createProtectedParagraphNode } from './Localizable/ProtectedParagraphNode.ts';

export type SerializedTabContainerNode = Spread<
  {
    templateColumns: string;
  },
  SerializedElementNode
>;

function $convertTabContainerElement(
  domNode: HTMLElement,
): DOMConversionOutput | null {
  const templateColumns = domNode.style.gridTemplateColumns;

  if (templateColumns) {
    const node = $createTabContainerNode(templateColumns.trim());
    return { node };
  }

  return null;
}

export class TabContainerNode extends LayoutContainerNode {
  __templateColumns: string;

  constructor(templateColumns: string, key?: NodeKey) {
    super(templateColumns, key);
    this.__templateColumns = templateColumns;
  }

  static getType(): string {
    return 'tab-container';
  }

  static clone(node: TabContainerNode): TabContainerNode {
    return new TabContainerNode(node.__templateColumns, node.__key);
  }

  createDOM(config: EditorConfig, editor: LexicalEditor): HTMLElement {
    const dom = document.createElement('div');
    dom.style.gridTemplateColumns = this.__templateColumns;

    const addNode = () => {
      editor.update(() => {
        const newTabNode = $createTabItemNode();
        const newParagraphNode = $createProtectedParagraphNode();
        const newLinkNode = $createLocalizationLinkNode();
        const newTextNode = $createLocalizableTextNode(
          i18n.t('Page.PageDesigner.new_tab'),
          null,
          null,
        );

        newLinkNode.append(newTextNode);
        newParagraphNode.append(newLinkNode);
        newTabNode.append(newParagraphNode);

        this.append(newTabNode);
        const writableNode = this.getWritable();
        writableNode.setTemplateColumns(
          writableNode.getTemplateColumns() + ' 1fr',
        );
      });
    };

    dom.append(createActionButton(addNode, 'addButton'));

    if (typeof config.theme.tabContainer === 'string') {
      addClassNamesToElement(dom, config.theme.tabContainer);
    }
    return dom;
  }

  exportDOM(): { element: HTMLDivElement } {
    const element = document.createElement('div') as HTMLDivElement;
    element.style.gridTemplateColumns = this.__templateColumns;
    element.setAttribute('data-lexical-tab-container', 'true');
    return { element };
  }

  static importDOM(): DOMConversionMap | null {
    return {
      div: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-tab-container')) {
          return null;
        }
        return {
          conversion: $convertTabContainerElement,
          priority: 3,
        };
      },
    };
  }

  static importJSON(json: SerializedTabContainerNode): TabContainerNode {
    return $createTabContainerNode(json.templateColumns);
  }

  exportJSON(): SerializedTabContainerNode {
    return {
      ...super.exportJSON(),
      templateColumns: this.__templateColumns,
      type: 'tab-container',
      version: 1,
    };
  }
}

export function $createTabContainerNode(
  templateColumns: string,
): TabContainerNode {
  return new TabContainerNode(templateColumns);
}

export function $isTabContainerNode(
  node: LexicalNode | null | undefined,
): node is TabContainerNode {
  return node instanceof TabContainerNode;
}
